import { Listing } from '@app/models/Listing'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IListingSelectorState {
  readonly listings: List<Listing>
  readonly error?: string
  readonly isLoading: boolean
}

const ListingSelectorStateRecord = Record({
  listings: List<Listing>(),
  error: undefined,
  isLoading: false,
})

export class ListingSelectorState extends ListingSelectorStateRecord implements IListingSelectorState {
  public static fromJS(initialState?: Partial<IListingSelectorState>) {
    if (!initialState) {
      return new ListingSelectorState()
    }

    const { listings, ...rest } = initialState
    return new ListingSelectorState({
      listings: listings ? List(listings) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly isLoading: boolean
  public readonly listings: List<Listing>

  constructor(props?: Partial<ListingSelectorState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ListingSelectorActionTypes {
  LoadListings = 'LISTING_SELECTOR::LOAD_LISTINGS',
  LoadListingsFailure = 'LISTING_SELECTOR::LOAD_LISTINGS_FAILURE',
  LoadListingsRequest = 'LISTING_SELECTOR::LOAD_LISTINGS_REQUEST',
  LoadListingsSuccess = 'LISTING_SELECTOR::LOAD_LISTINGS_SUCCESS',
}

interface ILoadListingsAction {
  filterPropertyId?: string
  loadComplete?: () => void
  type: ListingSelectorActionTypes.LoadListings
}

interface ILoadListingsFailureAction {
  error: string
  type: ListingSelectorActionTypes.LoadListingsFailure
}

interface ILoadListingsRequestAction {
  type: ListingSelectorActionTypes.LoadListingsRequest
}

interface ILoadListingsSuccessAction {
  listings: Listing[]
  type: ListingSelectorActionTypes.LoadListingsSuccess
}

type KnownAction =
  | ILoadListingsFailureAction
  | ILoadListingsSuccessAction
  | ILoadListingsRequestAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadListings(filterPropertyId?: string, loadComplete?: () => void): ILoadListingsAction {
  return {
    filterPropertyId,
    loadComplete,
    type: ListingSelectorActionTypes.LoadListings,
  }
}

function loadListingsFailure(error: string): ILoadListingsFailureAction {
  return {
    error,
    type: ListingSelectorActionTypes.LoadListingsFailure,
  }
}

function loadListingsRequest(): ILoadListingsRequestAction {
  return {
    type: ListingSelectorActionTypes.LoadListingsRequest,
  }
}

function loadListingsSuccess(listings: Listing[]): ILoadListingsSuccessAction {
  return {
    listings,
    type: ListingSelectorActionTypes.LoadListingsSuccess,
  }
}

export const actionCreators = {
  loadListings,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ListingSelectorState> = (
  state: ListingSelectorState = new ListingSelectorState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ListingSelectorActionTypes.LoadListingsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as ListingSelectorState

    case ListingSelectorActionTypes.LoadListingsRequest:
      return state.set('isLoading', true) as ListingSelectorState

    case ListingSelectorActionTypes.LoadListingsSuccess:
      const listings = List(action.listings)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('listings', listings) as ListingSelectorState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

function* loadListingsSaga(action: ILoadListingsAction) {
  try {
    yield put(loadListingsRequest())

    const currentState: ListingSelectorState = yield select(
      (state: ApplicationState) => state.listingSelector,
    )

    let query = ''

    if (action.filterPropertyId) {
      query = `?propertyId=${action.filterPropertyId}`
    }

    const url = `/api/Listings${query}`

    const result = yield makeAuthenticatedRequest(url)
    if (result.success === true) {
      yield put(loadListingsSuccess(result.results))
    } else {
      yield put(loadListingsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadListingsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadListings() {
  yield takeLatest(ListingSelectorActionTypes.LoadListings, loadListingsSaga)
}

export function* listingSelectorRootSaga() {
  yield fork(watchLoadListings)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): IListingSelectorState =>
    state.listingSelector

export const selectListings = () =>
  createSelector(selectModuleState(), (mState) => mState.listings)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)
